import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import MainCard from 'components/MainCard';
import useGet from 'hooks/useGet';

import {
  formatCurrency,
  formatDateTime,
  getCollection,
  normalizeInvoice,
  normalizeOrder,
  normalizeProduct,
  toTitleCase
} from './helpers';

const StatCard = ({ title, value, helper }) => (
  <MainCard>
    <Stack spacing={0.5}>
      <Typography variant="subtitle2" color="text.secondary">
        {title}
      </Typography>
      <Typography variant="h4">{value}</Typography>
      {helper ? (
        <Typography variant="body2" color="text.secondary">
          {helper}
        </Typography>
      ) : null}
    </Stack>
  </MainCard>
);

export default function CustomProductsOverview() {
  const productsRequest = useGet('/api/admin/custom-products?limit=100');
  const ordersRequest = useGet('/api/admin/custom-product-payments/orders?limit=10');
  const invoicesRequest = useGet('/api/admin/custom-product-invoices?limit=10');

  const loading =
    productsRequest.loading || ordersRequest.loading || invoicesRequest.loading;
  const error = productsRequest.error || ordersRequest.error || invoicesRequest.error;

  const products = getCollection(productsRequest.data, ['products']).map(normalizeProduct);
  const orders = getCollection(ordersRequest.data, ['orders']).map(normalizeOrder);
  const invoices = getCollection(invoicesRequest.data, ['invoices']).map(normalizeInvoice);

  const publishedCount = products.filter((product) => product.isPublished).length;
  const activeCount = products.filter((product) => product.isActive).length;
  const payNowCount = products.filter((product) => product.payNow).length;
  const capturedRevenue = orders
    .filter((order) => String(order.paymentStatus).toLowerCase() === 'captured')
    .reduce((sum, order) => sum + Number(order.totalAmount || 0), 0);
  const primaryCurrency = orders[0]?.currency || invoices[0]?.currency || 'INR';

  return (
    <Stack spacing={2.5}>
      <Stack spacing={0.5}>
        <Typography variant="h4">Custom Product Overview</Typography>
        <Typography color="text.secondary">
          A quick view of catalog health, recent payments, and invoice activity.
        </Typography>
      </Stack>

      {loading ? <LinearProgress /> : null}
      {error ? (
        <Alert severity="error">
          Failed to load one or more custom product admin sections.
        </Alert>
      ) : null}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard title="Products" value={products.length} helper={`${publishedCount} published`} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard title="Active Products" value={activeCount} helper={`${payNowCount} pay-now enabled`} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Orders"
            value={Number(ordersRequest.data?.total || orders.length)}
            helper="Recent admin payment orders"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Captured Revenue"
            value={formatCurrency(primaryCurrency, capturedRevenue)}
            helper="Calculated from the loaded captured orders"
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: 6 }}>
          <MainCard title={`Recent Orders (${orders.length})`}>
            <Stack spacing={1.5}>
              {orders.map((order) => (
                <Stack
                  key={order.id}
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  spacing={1}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1.5
                  }}
                >
                  <Stack spacing={0.3}>
                    <Typography variant="subtitle2">
                      {order.customProduct?.title || 'Custom product order'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {order.customerEmail || order.customerName || 'Customer not available'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDateTime(order.createdAt)}
                    </Typography>
                  </Stack>
                  <Stack spacing={0.8} alignItems={{ xs: 'flex-start', sm: 'flex-end' }}>
                    <Typography variant="subtitle2">
                      {formatCurrency(order.currency, order.totalAmount)}
                    </Typography>
                    <Chip
                      size="small"
                      label={toTitleCase(order.paymentStatus)}
                      color={
                        String(order.paymentStatus).toLowerCase() === 'captured'
                          ? 'success'
                          : String(order.paymentStatus).toLowerCase() === 'failed'
                            ? 'error'
                            : 'default'
                      }
                    />
                  </Stack>
                </Stack>
              ))}
              {!orders.length ? (
                <Typography color="text.secondary">No custom product orders found.</Typography>
              ) : null}
            </Stack>
          </MainCard>
        </Grid>

        <Grid size={{ xs: 12, lg: 6 }}>
          <MainCard title={`Recent Invoices (${invoices.length})`}>
            <Stack spacing={1.5}>
              {invoices.map((invoice) => (
                <Stack
                  key={invoice.id}
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  spacing={1}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1.5
                  }}
                >
                  <Stack spacing={0.3}>
                    <Typography variant="subtitle2">
                      {invoice.invoiceNumber || invoice.id}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {invoice.customProduct?.title || 'Custom product'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDateTime(invoice.createdAt)}
                    </Typography>
                  </Stack>
                  <Stack spacing={0.8} alignItems={{ xs: 'flex-start', sm: 'flex-end' }}>
                    <Typography variant="subtitle2">
                      {formatCurrency(invoice.currency, invoice.totalAmount)}
                    </Typography>
                    <Chip size="small" label={toTitleCase(invoice.status)} />
                  </Stack>
                </Stack>
              ))}
              {!invoices.length ? (
                <Typography color="text.secondary">No custom product invoices found.</Typography>
              ) : null}
            </Stack>
          </MainCard>
        </Grid>
      </Grid>
    </Stack>
  );
}
